/*
  Times in plain words. Every time is shown in the time zone of the browser, except in the tests, which pass one.
*/

export interface TimeOptions {
  timeZone?: string;
}

const LOCALE = "en-GB";

function clock(date: Date, timeZone?: string): string {
  return new Intl.DateTimeFormat(LOCALE, { timeZone, hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(date);
}

function weekday(date: Date, timeZone?: string): string {
  return new Intl.DateTimeFormat(LOCALE, { timeZone, weekday: "long" }).format(date);
}

function dayAndMonth(date: Date, timeZone?: string): string {
  return new Intl.DateTimeFormat(LOCALE, { timeZone, day: "numeric", month: "short" }).format(date);
}

function year(date: Date, timeZone?: string): number {
  return Number(new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric" }).format(date));
}

/** The calendar day of a moment as a whole number of days, in the zone asked for. */
function dayNumber(date: Date, timeZone?: string): number {
  const parts = new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).format(date).split("-");
  const [y, m, d] = parts.map(Number);
  return Math.floor(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1) / 86_400_000);
}

/**
 * "just now", "5 min ago", "today 14:10", "yesterday 14:10", "Tuesday 14:10", "12 Oct 14:10".
 * An empty text for a time that cannot be read.
 */
export function formatWhen(ts: string | null | undefined, now: Date, options: TimeOptions = {}): string {
  if (!ts) return "";
  const then = new Date(ts);
  if (Number.isNaN(then.getTime())) return "";
  const { timeZone } = options;
  const diff = now.getTime() - then.getTime();
  if (diff >= -60_000 && diff < 45_000) return "just now";
  if (diff >= 0 && diff < 3_600_000) return `${Math.max(1, Math.round(diff / 60_000))} min ago`;
  const days = dayNumber(now, timeZone) - dayNumber(then, timeZone);
  const time = clock(then, timeZone);
  if (days === 0) return `today ${time}`;
  if (days === 1) return `yesterday ${time}`;
  if (days > 1 && days < 7) return `${weekday(then, timeZone)} ${time}`;
  const sameYear = year(then, timeZone) === year(now, timeZone);
  return `${dayAndMonth(then, timeZone)}${sameYear ? "" : ` ${year(then, timeZone)}`} ${time}`;
}

/** The full day and time of a moment, for a slider label: "Tuesday 14 Oct, 14:10". */
export function formatMoment(ts: string | null | undefined, options: TimeOptions = {}): string {
  if (!ts) return "";
  const date = new Date(ts);
  if (Number.isNaN(date.getTime())) return "";
  const { timeZone } = options;
  return `${weekday(date, timeZone)} ${dayAndMonth(date, timeZone)}, ${clock(date, timeZone)}`;
}

/** How long a hand-over has waited: "less than a day", "1 day", "3 days". */
export function ageWords(days: number): string {
  if (days <= 0) return "less than a day";
  if (days === 1) return "1 day";
  return `${days} days`;
}
